/**
 * The detail page's actions: declared actions and emulator operations with a
 * Run button each, plus the sandbox's run history.
 *
 * @module features/sandboxes/components/v2/detail/SandboxActionsSection
 */
import React from 'react';
import { Button, Flex, Heading, LoadingOrbit, Text } from '@chatsdet/ui';
import { CaretDown, CaretRight, Play } from '@phosphor-icons/react';
import { formatDurationMs } from '../../../../../lib/formatDuration';
import {
  useSandboxV2Run,
  useSandboxV2RunHistory,
  type SandboxV2Detail,
  type SandboxV2RunListItem,
} from '../../../hooks/useSandboxV2Detail';
import { formatRelative } from '../../overview/time-format';
import { RunStatusChip } from '../sandboxV2Display';
import { declaredActionToRunnable, operationToRunnable, type RunnableAction } from './actionForm';
import { RunActionDialog } from './RunActionDialog';
import { RunResultView } from './RunResultView';

function ActionRows({
  title,
  actions,
  onRun,
  disabled,
}: {
  title: string;
  actions: RunnableAction[];
  onRun: (action: RunnableAction) => void;
  disabled: boolean;
}) {
  if (actions.length === 0) return null;
  return (
    <div>
      <Text size="xs" color="muted" className="mb-1 uppercase tracking-wide">
        {title}
      </Text>
      <div className="divide-y divide-border">
        {actions.map((action) => (
          <Flex key={action.ref} align="center" gap="sm" className="py-1.5">
            <div className="min-w-0 flex-1">
              <Text weight="medium" size="sm" className="font-mono">
                {action.ref}
              </Text>
              <Text size="sm" color="muted" className="text-pretty">
                {action.description}
              </Text>
            </div>
            <Button variant="outline" size="sm" isDisabled={disabled} onPress={() => onRun(action)} aria-label={`Run ${action.ref}`}>
              <Play size={12} weight="fill" />
              Run
            </Button>
          </Flex>
        ))}
      </div>
    </div>
  );
}

function RunHistoryRow({ run }: { run: SandboxV2RunListItem }) {
  const [open, setOpen] = React.useState(false);
  const detail = useSandboxV2Run(open ? run.id : null);
  const inputs = Object.entries(run.input);
  return (
    <div className="py-1.5">
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 text-left"
      >
        {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
        <span className="font-mono text-sm">{run.action}</span>
        <RunStatusChip status={run.status} />
        <Text size="xs" color="muted" className="min-w-0 flex-1 truncate font-mono">
          {inputs.map(([key, value]) => `${key}=${String(value)}`).join(' ')}
        </Text>
        <Text size="xs" color="muted" className="tabular-nums">
          {formatDurationMs(run.durationMs)} · {formatRelative(run.createdAt)}
        </Text>
      </button>
      {open && (
        <div className="mt-2 pl-5">
          {detail.data ? (
            <RunResultView run={detail.data} />
          ) : detail.isError ? (
            <Text size="sm" color="destructive">
              {detail.error.message}
            </Text>
          ) : (
            <LoadingOrbit size="sm" />
          )}
        </div>
      )}
    </div>
  );
}

function RunHistory({ sandboxId }: { sandboxId: string }) {
  const history = useSandboxV2RunHistory(sandboxId, true);
  const runs = history.data?.pages.flatMap((page) => page.data) ?? [];
  return (
    <div>
      <Text size="xs" color="muted" className="mb-1 uppercase tracking-wide">
        Run history
      </Text>
      {history.isLoading ? (
        <LoadingOrbit size="sm" />
      ) : history.isError ? (
        <Text size="sm" color="destructive">
          {history.error.message}
        </Text>
      ) : runs.length === 0 ? (
        <Text size="sm" color="muted">
          No runs yet.
        </Text>
      ) : (
        <div className="divide-y divide-border">
          {runs.map((run) => (
            <RunHistoryRow key={run.id} run={run} />
          ))}
        </div>
      )}
      {history.hasNextPage && (
        <Button variant="outline" size="sm" className="mt-2" onPress={() => history.fetchNextPage()} isDisabled={history.isFetchingNextPage}>
          {history.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </Button>
      )}
    </div>
  );
}

export function SandboxActionsSection({ sandbox }: { sandbox: SandboxV2Detail }) {
  const [running, setRunning] = React.useState<RunnableAction | null>(null);
  const actions = React.useMemo(() => sandbox.actions.map(declaredActionToRunnable), [sandbox.actions]);
  const operations = React.useMemo(() => sandbox.operations.map(operationToRunnable), [sandbox.operations]);
  const personas = React.useMemo(() => sandbox.personas.map((persona) => persona.name), [sandbox.personas]);

  return (
    <section className="flex flex-col gap-3" aria-labelledby="sandbox-v2-actions">
      <Heading level={3} id="sandbox-v2-actions">
        Actions
      </Heading>
      {actions.length === 0 && operations.length === 0 && (
        <Text size="sm" color="muted">
          This sandbox declares no actions.
        </Text>
      )}
      {sandbox.expired && (
        <Text size="sm" color="muted">
          This sandbox's lease has expired, so actions can no longer run.
        </Text>
      )}
      <ActionRows title="Actions" actions={actions} onRun={setRunning} disabled={sandbox.expired} />
      <ActionRows title="Emulator operations" actions={operations} onRun={setRunning} disabled={sandbox.expired} />
      <RunHistory sandboxId={sandbox.id} />
      <RunActionDialog action={running} personas={personas} sandboxId={sandbox.id} onClose={() => setRunning(null)} />
    </section>
  );
}
